import {
	type QueryClient,
	useMutation,
	useQueryClient,
} from "@tanstack/react-query"
import { isHttpError, isNetworkError } from "@/shared/api/errors"
import { eiKeys } from "../model/keys"
import type { EiSaveAnswers } from "../model/types"
import {
	completeEiTest,
	saveEiAnswers,
	setEiVisibility,
	startEiTest,
} from "./client"
import {
	eiProfileQueryOptions,
	eiResultQueryOptions,
	eiSessionQueryOptions,
} from "./queries"

function markSessionCompleted(queryClient: QueryClient) {
	const { queryKey } = eiSessionQueryOptions()
	queryClient.setQueryData(queryKey, (old) => {
		if (!old) return old
		return { ...old, status: "completed" as const }
	})
	queryClient.invalidateQueries({ queryKey: eiKeys.profiles() })
	queryClient.invalidateQueries({ queryKey: eiKeys.leaderboards() })
}

function syncAlreadyCompleted(queryClient: QueryClient, error: Error) {
	if (!isHttpError(error) || error.status !== 409) return
	queryClient.removeQueries({ queryKey: eiKeys.result() })
	markSessionCompleted(queryClient)
}

export function useStartEiTest() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (restart: boolean) => startEiTest(restart),
		onSuccess: (session) => {
			queryClient.setQueryData(eiSessionQueryOptions().queryKey, session)
			queryClient.removeQueries({ queryKey: eiKeys.result() })
		},
		onError: (error) => syncAlreadyCompleted(queryClient, error),
	})
}

export function useSaveEiAnswers() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (body: EiSaveAnswers) => saveEiAnswers(body),
		onSuccess: (saved, body) => {
			const { queryKey } = eiSessionQueryOptions()
			queryClient.setQueryData(queryKey, (old) => {
				if (!old) return old
				const answers = { ...old.answers, [body.group_id]: body.answers }
				return { ...old, ...saved, answers }
			})
		},
		onError: (error) => syncAlreadyCompleted(queryClient, error),
	})
}

export function useCompleteEiTest() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: completeEiTest,
		onSuccess: (result) => {
			queryClient.setQueryData(eiResultQueryOptions().queryKey, result)
			markSessionCompleted(queryClient)
		},
		onError: (error) => {
			if (!isNetworkError(error)) return
			queryClient.invalidateQueries({
				queryKey: eiSessionQueryOptions().queryKey,
			})
		},
	})
}

export function useSetEiVisibility(userId: number) {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (visible: boolean) => setEiVisibility(userId, visible),
		onMutate: async (visible) => {
			const { queryKey } = eiProfileQueryOptions(userId)
			await queryClient.cancelQueries({ queryKey })
			const previous = queryClient.getQueryData(queryKey)
			queryClient.setQueryData(queryKey, (old) => {
				if (old?.status !== "completed") return old
				return { ...old, visible_to_others: visible }
			})
			return { previous }
		},
		onError: (_error, _visible, context) => {
			if (!context?.previous) return
			const { queryKey } = eiProfileQueryOptions(userId)
			queryClient.setQueryData(queryKey, context.previous)
		},
	})
}
